'use client';

/**
 * 【v6.97.19 2026-07-14】AI规划课程按钮悬浮提示
 *
 * 作用: 鼠标悬停在「AI规划课程」按钮上时,显示该按钮的两个核心功能点。
 *
 * 设计参考: Linear / Vercel / Cursor 风格的玻璃态 hover card。
 * - 280px 固定宽度 + max-w-[calc(100vw-2rem)] 兜底(极窄屏)
 * - collisionPadding={16} 距屏幕边 ≥16px
 * - Radix 内置 avoidCollisions=true,空间不足自动翻转 side
 * - 暗色模式: useTheme 控制,直接用 dark: 变体
 *
 * 行为:
 * - 桌面 hover: openDelay=200ms, closeDelay=100ms
 * - 触屏: 按钮点击行为不变(跳转 /dongniai/plan/myForms);
 *         提示信息通过按钮文字「AI规划课程」+ 用户已知场景传达
 *         (不与点击冲突,navigate 立即发生,tooltip 来不及显示 = 无副作用)
 *
 * 注意: 不是 action menu,无内嵌按钮,无键盘提示 footer;
 *       按钮本体的 onClick 与 data-plan-btn 行为完全保留
 *       (Dialog 收缩动画仍可通过 [data-plan-btn] 定位)。
 */

import * as React from 'react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Sparkles, Wand2, GraduationCap } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AiPlanTooltipProps {
  children: React.ReactNode;
  className?: string;
}

export function AiPlanTooltip({ children, className }: AiPlanTooltipProps) {
  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        {children}
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="end"
        sideOffset={10}
        collisionPadding={16}
        className={cn(
          // 尺寸: 280px 基础, 极窄屏自适应
          'w-[280px] max-w-[calc(100vw-2rem)] p-3.5',
          // 形状
          'rounded-2xl',
          // 玻璃态
          'bg-white/95 dark:bg-slate-900/95',
          'backdrop-blur-xl',
          // 边框
          'border border-slate-200/80 dark:border-slate-700/60',
          // 阴影(品牌色淡阴影)
          'shadow-2xl shadow-blue-500/10 dark:shadow-blue-500/20',
          // 描边增强
          'ring-1 ring-slate-900/5 dark:ring-white/5',
          // 内容溢出保护
          'overflow-hidden',
          className
        )}
      >
        {/* 顶部高光带 — 模拟玻璃高光(大厂标配细节) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-8 rounded-t-2xl bg-gradient-to-b from-blue-400/15 to-transparent"
        />

        {/* 底部品牌色细线 — 增强立体感(可选) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-3 bottom-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent"
        />

        {/* 标题行:Sparkles 渐变小图标 + 渐变文字 */}
        <div className="relative flex items-center gap-2 mb-2.5">
          <div className="flex size-5 items-center justify-center rounded-md bg-gradient-to-br from-blue-500 to-violet-500 shadow-sm shadow-blue-500/30">
            <Sparkles className="size-3 text-white" />
          </div>
          <div className="text-[13px] font-semibold leading-none bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
            AI 课程助手
          </div>
        </div>

        {/* 分隔线 */}
        <div
          aria-hidden="true"
          className="relative h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent dark:via-slate-700 mb-2.5"
        />

        {/* 功能点 1: 帮写提示词 · 一键填入输入框 */}
        <div className="relative flex items-center gap-2 mb-1.5">
          <Wand2 className="size-3.5 shrink-0 text-blue-500 dark:text-blue-400" />
          <div className="text-[12px] leading-relaxed text-slate-600 dark:text-slate-300">
            <span className="font-medium text-slate-900 dark:text-slate-100">
              帮写提示词
            </span>
            <span className="mx-1.5 text-slate-300 dark:text-slate-600">·</span>
            <span>一键填入输入框</span>
          </div>
        </div>

        {/* 功能点 2: 规划系列课 · 系统化学习路径 */}
        <div className="relative flex items-center gap-2">
          <GraduationCap className="size-3.5 shrink-0 text-violet-500 dark:text-violet-400" />
          <div className="text-[12px] leading-relaxed text-slate-600 dark:text-slate-300">
            <span className="font-medium text-slate-900 dark:text-slate-100">
              规划系列课
            </span>
            <span className="mx-1.5 text-slate-300 dark:text-slate-600">·</span>
            <span>系统化学习路径</span>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
